import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Flag,
  Loader2,
  Sparkles,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

/**
 * One-click Vision Feedback dialog mounted directly on a generated image card.
 * Skips the manual screenshot + sim_type/style picker friction of the
 * standalone admin tab. Pre-fills everything from the simulation context.
 *
 * Visible only for admin/sales (gated by parent).
 */
export default function VisionFeedbackDialog({ sim, image }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [autoApply, setAutoApply] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setDescription("");
    setResult(null);
    setAutoApply(true);
  };

  const submit = async () => {
    if (!image?.url) {
      toast.error(t("vision.err_image_missing"));
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      // Pull the generated image bytes from storage so we can re-post it
      // as a file to the existing vision-feedback endpoint.
      const fullUrl = fileUrl(image.url);
      const blob = await fetch(fullUrl, {
        cache: "no-store",
      }).then((r) => {
        if (!r.ok) throw new Error(`Fetch image failed (${r.status})`);
        return r.blob();
      });

      const fd = new FormData();
      fd.append(
        "file",
        new File([blob], `sim-${sim.id}-img-${image.id || "x"}.jpg`, {
          type: blob.type || "image/jpeg",
        }),
      );
      fd.append("description", description);
      fd.append("sim_type", sim.sim_type);
      fd.append("style", sim.style);
      fd.append("auto_apply", autoApply ? "true" : "false");

      const { data } = await api.post("/admin/feedback/vision", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.auto_applied) {
        toast.success(
          t("vision.toast_auto_applied", { sim_type: sim.sim_type, style: sim.style }),
        );
      } else {
        toast.message(t("vision.toast_suggestion"));
      }
    } catch (e) {
      const msg =
        formatApiErrorDetail(e.response?.data?.detail) ||
        e.message ||
        t("vision.toast_generic_error");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const applyManually = async () => {
    if (!result?.suggested_anchor) return;
    setBusy(true);
    try {
      await api.put(`/admin/prompts/${sim.sim_type}/${sim.style}`, {
        anchor: result.suggested_anchor,
      });
      toast.success(t("vision.toast_applied", { sim_type: sim.sim_type, style: sim.style }));
      setResult({ ...result, auto_applied: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("vision.toast_generic_error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setOpen(true);
          reset();
        }}
        className="px-2 py-1 rounded text-[10px] uppercase tracking-wider font-bold transition bg-white/5 text-zinc-400 hover:bg-rose-500/15 hover:text-rose-300 hover:border-rose-500/30 border border-white/10 flex items-center gap-1"
        title={t("vision.btn_title")}
        data-testid="vision-flag-btn"
      >
        <Flag size={10} /> {t("vision.btn")}
      </button>

      <Dialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) reset();
        }}
      >
        <DialogContent
          className="bg-zinc-950 border-emerald-500/30 max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="vision-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white font-outfit flex items-center gap-2">
              <Sparkles className="text-emerald-400" size={18} />
              {t("vision.dialog_title")}
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">
              {t("vision.dialog_desc_prefix")}{" "}
              <span className="text-white font-medium">{sim.sim_type}</span> /{" "}
              <span className="text-white font-medium">{sim.style}</span>.
            </DialogDescription>
          </DialogHeader>

          {/* Explicație flow — cere Marius 03.06.2026 */}
          {!result && (
            <div
              className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-[12px] text-zinc-300 leading-relaxed"
              data-testid="vision-dialog-explanation"
            >
              <div className="font-semibold text-emerald-300 mb-1.5">
                {t("vision.how_title")}
              </div>
              <ul className="list-disc list-outside pl-5 space-y-1">
                <li>
                  {t("vision.how_li_1_prefix")} <strong>{t("vision.how_li_1_bold")}</strong>{" "}
                  {t("vision.how_li_1_suffix")}
                </li>
                <li>
                  {t("vision.how_li_2_prefix")} <strong>{t("vision.how_li_2_bold")}</strong>{" "}
                  {t("vision.how_li_2_mid", { sim_type: sim.sim_type, style: sim.style })}{" "}
                  <strong>{t("vision.how_li_2_bold2")}</strong> {t("vision.how_li_2_suffix")}
                </li>
                <li>
                  {t("vision.how_li_3")}
                </li>
              </ul>
            </div>
          )}

          {/* Image preview */}
          <div className="rounded-lg overflow-hidden border border-white/10 bg-black">
            <img
              src={fileUrl(image.url)}
              alt="render"
              className="w-full max-h-[300px] object-contain"
              data-testid="vision-dialog-preview"
            />
          </div>

          {/* Description input */}
          {!result && (
            <div>
              <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide block mb-2">
                {t("vision.desc_label")}
              </label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("vision.desc_placeholder")}
                rows={3}
                maxLength={500}
                className="bg-zinc-900 border-white/10 text-white text-sm resize-none focus-visible:ring-emerald-500/40"
                data-testid="vision-dialog-description"
              />
            </div>
          )}

          {/* Auto-apply checkbox */}
          {!result && (
            <label className="flex items-start gap-3 p-3 border border-white/10 bg-zinc-950/40 rounded-md cursor-pointer hover:border-white/20 transition">
              <input
                type="checkbox"
                checked={autoApply}
                onChange={(e) => setAutoApply(e.target.checked)}
                className="mt-0.5 accent-emerald-500"
                data-testid="vision-dialog-auto-apply"
              />
              <div className="flex-1">
                <div className="text-sm text-white font-medium">
                  {t("vision.auto_apply_title")}
                </div>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  {t("vision.auto_apply_hint")}
                </div>
              </div>
            </label>
          )}

          {/* Action buttons */}
          {!result && (
            <Button
              onClick={submit}
              disabled={busy}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
              data-testid="vision-dialog-submit"
            >
              {busy ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  {t("vision.submit_loading")}
                </>
              ) : (
                <>
                  <Sparkles size={14} className="mr-2" />
                  {t("vision.submit_btn")}
                </>
              )}
            </Button>
          )}

          {/* Result panel */}
          {result && (
            <div className="space-y-4" data-testid="vision-dialog-result">
              <div className="flex items-center gap-2 flex-wrap">
                {result.auto_applied ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs font-medium">
                    <CheckCircle2 size={13} /> {t("vision.badge_applied")}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-orange-500/15 border border-orange-500/40 text-orange-300 text-xs font-medium">
                    <AlertCircle size={13} /> {t("vision.badge_suggestion")}
                  </span>
                )}
                <span className="text-xs text-zinc-500">
                  {t("vision.confidence_label")}{" "}
                  <strong className="text-white">
                    {Math.round((result.confidence || 0) * 100)}%
                  </strong>
                </span>
              </div>

              <div className="rounded-lg border border-white/10 bg-zinc-950/40 p-3">
                <p className="text-[11px] text-zinc-500 uppercase tracking-wider mb-1">
                  {t("vision.detected_label")}
                </p>
                <p className="text-sm text-white font-medium">
                  {result.detected_reason_label}
                </p>
              </div>

              {result.observation && (
                <div className="rounded-lg border border-white/10 bg-zinc-950/40 p-3">
                  <p className="text-[11px] text-zinc-500 uppercase tracking-wider mb-1">
                    {t("vision.observation_label")}
                  </p>
                  <p className="text-sm text-zinc-300 leading-relaxed">
                    {result.observation}
                  </p>
                </div>
              )}

              <div className="rounded-lg border border-zinc-700 bg-zinc-900/50 p-3">
                <p className="text-[11px] text-zinc-500 uppercase tracking-wider mb-1">
                  {t("vision.current_label")}
                </p>
                <p className="text-xs text-zinc-300 italic">
                  "{result.current_anchor}"
                </p>
              </div>

              <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3">
                <p className="text-[11px] text-emerald-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  <Sparkles size={11} /> {t("vision.suggested_label")}
                </p>
                <p className="text-sm text-white font-medium">
                  "{result.suggested_anchor}"
                </p>
                {result.reasoning && (
                  <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
                    💡 {result.reasoning}
                  </p>
                )}
              </div>

              {!result.auto_applied && (
                <Button
                  onClick={applyManually}
                  disabled={busy}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="vision-dialog-apply-manual"
                >
                  {busy ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    t("vision.apply_manual_btn", { sim_type: sim.sim_type, style: sim.style })
                  )}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
